// Direct paths, not the application barrel: these files are inside it, and importing it from
// itself is a cycle (see CasPendingEmailsPill in CasContextProviders).
import {
  MAX_SMS_RECIPIENTS,
  RecipientOptionType,
  SendAsType,
  SendChannelType,
  channelOf,
  supportsChannel,
} from '../composeMode';

/*
  One row of a Batch Send - one message, to one thing.

  Deliberately says nothing about WHAT that thing is. SCE Reports batches by insured and Customers
  by customer, and everything in this folder works the same way for both: the row carries a key to
  file its overrides under, a label to show, the id the message hangs off, and the two per-channel
  counts the list is judged on.
*/
export interface IBatchRow {
  /* Stable identity: the insured name on SCE Reports, the entity id on Customers. */
  key: string;

  /* What the list shows for this row. */
  label: string;

  /*
    The record the message is written against - a grower id, or a customer id. Null means the row
    CANNOT be sent: there would be nothing to write msg.History against.
  */
  id: number | null;

  /*
    Split by channel: the browser decides each message's channel and never loads a row's actual
    contacts unless the user opens it, so for every other row these two numbers are all it has.
  */
  emailContactCount: number;
  phoneContactCount: number;

  /*
    How many of the row's default contacts can be reached on each channel AT ALL, whatever they
    prefer. A different question from the two counts above, which describe what Auto resolves to.

    Only these can answer "the sender pinned this row to SMS - is there a phone number anywhere?",
    because pinning overrides the preference the counts above were built from. Optional: a row
    source that does not supply them simply gets no pinned-channel check, which is what the code
    did before they existed.
  */
  emailReachableCount?: number;
  phoneReachableCount?: number;
}

/*
  The words a batch uses for its own subject matter, so the reasons a row cannot be sent read as
  that page's language rather than as the other page's.
*/
export interface IBatchTerms {
  /* Singular, lower case: "insured", "customer". Used in "...for this {noun}". */
  noun: string;

  /* Why a row with no id cannot be sent at all. */
  missingId: string;

  /* Why a row with no reachable contacts cannot be sent. */
  noContacts: string;

  /* How the row's own contacts are named when they disagree - "SCE contacts", "Contacts". */
  contactsLabel: string;

  /* Shown in place of the list when the filters match nothing. */
  emptyList: string;
}

/*
  What channel this row's message goes on, or why that question has no answer yet.

  One message goes out on one channel, so a row whose recipients do not agree on one cannot be
  sent as it stands - and is reported rather than quietly dropped or half-delivered, because the
  sender is the only one who can decide what they meant. Every unresolved row blocks the batch
  until it is settled: pin the row's channel, edit its recipients, or take the row out.
*/
export type RowChannelType =
  | { channel: SendChannelType; reason?: undefined }
  | { channel?: undefined; reason: string };

// A row's recipients as the sender has them: the open row's live fields, or its committed edits.
export type EditedRecipientsType = { to: RecipientOptionType[]; cc: RecipientOptionType[] };

const channelFromRecipients = (
  row: IBatchRow,
  terms: IBatchTerms,
  sendAs: SendAsType = 'auto',
  edited?: EditedRecipientsType
): RowChannelType => {
  if (row.id == null) return { reason: terms.missingId };

  /*
    Pinned. The sender has overridden whatever the contacts prefer, so the message is addressed on
    this channel - which is only possible if somebody has an address on it. A row the sender opened
    carries its real recipients and is judged on those; every other row is judged on what the list
    endpoint said was reachable.

    Reported rather than left to the send: resolving to nobody server-side skips the message, and
    a batch that quietly posts three of the four messages the sender ticked is the worst way to
    find that out.
  */
  if (sendAs !== 'auto') {
    if (edited) {
      /*
        Pinning prunes To down to the contacts who can take the channel, so a row whose contacts are
        all on the other one is left with nobody - and while the row is still open, nothing else has
        caught it yet: the empty-row check only reads rows already left. Only To counts, because a
        CC alone sends nothing - the server skips a message with no To.
      */
      if (!edited.to.some((option) => supportsChannel(option, sendAs))) {
        if (edited.to.length === 0) return { reason: 'No recipient selected' };

        return {
          reason:
            sendAs === 'email'
              ? 'Pinned to Email, but no recipient has an email address'
              : 'Pinned to SMS, but no recipient has a phone number',
        };
      }
    } else {
      const reachable =
        sendAs === 'email' ? row.emailReachableCount : row.phoneReachableCount;

      if (reachable === 0) {
        return {
          reason:
            sendAs === 'email'
              ? 'Pinned to Email, but no contact included by default has an email address'
              : 'Pinned to SMS, but no contact included by default has a phone number',
        };
      }
    }

    return { channel: sendAs };
  }

  /*
    `edited` is present only for a row the user opened and changed; those carry real contacts, which
    can include an employee reachable both ways. Every other row is judged on the two counts the
    list endpoint supplied, where a contact is one or the other and never both.
  */
  if (edited) {
    // channelOf returns null for a contact reachable both ways, which settles nothing on its own.
    const channels = new Set(edited.to.map(channelOf).filter(Boolean) as SendChannelType[]);

    if (channels.size > 1) {
      return { reason: `Recipients mix email and phone - pick Email or SMS for this ${terms.noun}` };
    }
    if (channels.size === 1) return { channel: Array.from(channels)[0] };
    if (edited.to.length > 0) {
      return {
        reason: `Every recipient can take either - pick Email or SMS for this ${terms.noun}`,
      };
    }
    return { reason: 'No recipient selected' };
  }

  const { emailContactCount: emails, phoneContactCount: phones } = row;

  if (emails === 0 && phones === 0) return { reason: terms.noContacts };
  if (emails > 0 && phones > 0) {
    return {
      reason: `${terms.contactsLabel} mix email and phone - pick Email or SMS for this ${terms.noun}`,
    };
  }

  return { channel: emails > 0 ? 'email' : 'sms' };
};

export const getRowChannel = (
  row: IBatchRow,
  terms: IBatchTerms,
  sendAs: SendAsType = 'auto',
  edited?: EditedRecipientsType
): RowChannelType => {
  const resolved = channelFromRecipients(row, terms, sendAs, edited);

  if (resolved.channel !== 'sms') return resolved;

  /*
    A text naming more than the carrier's group limit fails at RingCentral, after the batch has
    been queued. Caught here so the row blocks the send instead - including a row nobody opened,
    whose phone contacts the server would otherwise resolve and send all at once. To and CC count
    together, as they do on the server (the phone contact count already covers both).
  */
  const smsRecipients = (() => {
    if (edited) {
      return [...edited.to, ...edited.cc].filter((option) => supportsChannel(option, 'sms')).length;
    }

    // A PINNED row is addressed on the phone channel whatever the contacts prefer, so what the
    // carrier will see is the reachable count, not the one Auto would have resolved to.
    return sendAs === 'sms'
      ? row.phoneReachableCount ?? row.phoneContactCount
      : row.phoneContactCount;
  })();

  if (smsRecipients > MAX_SMS_RECIPIENTS) {
    return {
      reason: `${smsRecipients} phone recipients (To and CC) - a text can reach at most ${MAX_SMS_RECIPIENTS}. Remove some, or pick Email`,
    };
  }

  return resolved;
};
